"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useVendorInvitations } from '@/hooks/useVendorInvitations';
import { VendorInvitationStats } from '@/components/vendor/VendorInvitationStats';
import { VendorInvitationsTable } from '@/components/vendor/VendorInvitationsTable';
import { InviteVendorDialog } from '@/components/vendor/InviteVendorDialog';
import { UserPlus, AlertCircle } from 'lucide-react';

export default function VendorInvitePage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    invitations,
    loading,
    dailyCount,
    stats,
    sendInvitation,
    cancelInvitation,
    reInvite,
    checkExistingVendor,
  } = useVendorInvitations();

  const dailyLimitReached = dailyCount >= 10;
  const dailyLimitWarning = dailyCount >= 8;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Invite Vendors</h1>
          <p className="text-muted-foreground mt-1">
            Invite vendors to join the platform and participate in your RFQs
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} disabled={dailyLimitReached}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Vendor
        </Button>
      </div>

      {dailyLimitWarning && (
        <Alert variant={dailyLimitReached ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {dailyLimitReached
              ? 'You have reached your daily invitation limit (10/10). The limit resets at midnight.'
              : `You have sent ${dailyCount} of 10 invitations today. ${10 - dailyCount} remaining.`}
          </AlertDescription>
        </Alert>
      )}

      <VendorInvitationStats stats={stats} />

      <Card>
        <CardHeader>
          <CardTitle>Sent Invitations</CardTitle>
          <CardDescription>
            View and manage all vendor invitations you've sent
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No invitations sent yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Start by inviting vendors to join the platform
              </p>
            </div>
          ) : (
            <VendorInvitationsTable
              invitations={invitations}
              onCancel={cancelInvitation}
              onReInvite={(email, message) => reInvite(email, message || undefined)}
            />
          )}
        </CardContent>
      </Card>

      <InviteVendorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={sendInvitation}
        checkExistingVendor={checkExistingVendor}
        dailyCount={dailyCount}
      />
    </div>
  );
}
